import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ArrowsClockwise, Eye, EyeSlash, Info, LinkSimple, Trash } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDuration } from "@/pages/Resources";

const EMPTY_FORM = { title: "", description: "", access: "all", vimeo_url: "" };

export function ResourcesAdminSection() {
  const [config, setConfig] = useState(null);
  const [videos, setVideos] = useState([]);
  const [err, setErr] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [c, v] = await Promise.all([api.get(`/admin/resources/config`), api.get(`/admin/resources/videos`)]);
      setConfig(c.data); setVideos(v.data.videos);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load resources"); }
  };
  useEffect(() => { load(); }, []);

  const fail = (e, fallback) => setErr(formatApiErrorDetail(e.response?.data?.detail) || fallback);
  const replaceRow = (row) => setVideos((vs) => vs.map((x) => (x.id === row.id ? row : x)));

  const add = async (e) => {
    e.preventDefault(); setErr("");
    if (!form.title.trim()) { setErr("Give the video a title first."); return; }
    if (!form.vimeo_url.trim()) { setErr("Paste the Vimeo link."); return; }
    setSaving(true);
    try { const { data } = await api.post(`/admin/resources/videos`, form); setVideos((vs) => [...vs, data.video]); setForm(EMPTY_FORM); }
    catch (er) { fail(er, "Could not add video"); }
    finally { setSaving(false); }
  };
  const patch = async (id, body) => {
    try { const { data } = await api.put(`/admin/resources/videos/${id}`, body); replaceRow(data.video); }
    catch (e) { fail(e, "Save failed"); }
  };
  const refresh = async (id) => {
    try { const { data } = await api.post(`/admin/resources/videos/${id}/refresh`); replaceRow(data.video); }
    catch (e) { fail(e, "Refresh failed"); }
  };
  const move = async (i, dir) => {
    const next = [...videos]; const j = i + dir; if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]]; setVideos(next);
    await api.put(`/admin/resources/order`, { ids: next.map((v) => v.id) }).catch(() => {});
  };
  const remove = async (v) => {
    if (!window.confirm(`Remove "${v.title}" from the library? The video stays in your Vimeo account.`)) return;
    try { await api.delete(`/admin/resources/videos/${v.id}`); setVideos((vs) => vs.filter((x) => x.id !== v.id)); }
    catch (e) { fail(e, "Delete failed"); }
  };

  return (
    <div className="fade-up space-y-6" data-testid="admin-resources-section">
      <div><p className="label-uppercase">Library</p><h2 className="font-display text-3xl font-bold" style={{ color: "var(--foreground)" }}>Resources</h2>
        <p className="text-sm" style={{ color: "var(--muted)" }}>Recordings are hosted on Vimeo and play only inside the hub.</p></div>
      {config && (
        <div className="niche-card" data-testid="admin-resources-vimeo-help">
          <p className="font-semibold text-sm flex items-center gap-2" style={{ color: "var(--foreground)" }}><Info size={16} /> How to add a recording</p>
          <ol className="text-xs mt-1 space-y-1 list-decimal pl-4" style={{ color: "var(--muted)" }}>
            <li>Upload the video to Vimeo. In the video's <b>Privacy</b> settings choose <b>Hide from Vimeo</b>, and under <b>Where can this be embedded?</b> pick <b>Specific domains</b> and add: {(config.embed_domains || []).map((d) => <code key={d} className="mx-1">{d}</code>)}</li>
            <li>Copy the link from the video's <b>Share</b> dialog and paste it below. Duration and thumbnail are pulled automatically.</li>
          </ol>
        </div>
      )}
      {err && <p className="error-banner" data-testid="admin-resources-error">{err}</p>}

      <form className="panel" onSubmit={add} data-testid="admin-resources-add">
        <div className="panel-head"><div><p className="label-uppercase">Add video</p><h3 className="panel-title">Link a Vimeo recording</h3></div></div>
        <div className="p-4 grid gap-3 sm:grid-cols-2">
          <input className="input" placeholder="Title (e.g. Bootcamp 1 — Full recording)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-video-title" />
          <select className="input" value={form.access} onChange={(e) => setForm({ ...form, access: e.target.value })} data-testid="admin-video-access">
            <option value="all">Visible to all participants</option>
            <option value="mentorship">Mentorship only (onboarded)</option>
          </select>
          <input className="input sm:col-span-2" placeholder="Vimeo link — https://vimeo.com/123456789 or https://vimeo.com/123456789/abcdef1234" value={form.vimeo_url} onChange={(e) => setForm({ ...form, vimeo_url: e.target.value })} data-testid="admin-video-url" />
          <textarea className="input sm:col-span-2" rows={2} placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-video-description" />
          <div className="sm:col-span-2 flex items-center gap-3 flex-wrap">
            <button type="submit" className="btn btn-accent btn-sm" disabled={saving} data-testid="admin-video-add">
              <LinkSimple size={14} weight="bold" /> {saving ? "Adding…" : "Add to library"}
            </button>
            <span className="text-xs" style={{ color: "var(--muted)" }}>Any length — 3 h and 6 h sessions stream fine. Vimeo handles encoding and adaptive quality.</span>
          </div>
        </div>
      </form>

      <section className="panel" data-testid="admin-resources-list">
        <div className="panel-head"><div><p className="label-uppercase">{videos.length} video{videos.length === 1 ? "" : "s"}</p><h3 className="panel-title">Library order</h3></div></div>
        {videos.length === 0 ? <p className="p-6 text-sm" style={{ color: "var(--muted)" }}>No videos yet.</p> : (
          <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
            {videos.map((v, i) => (
              <li key={v.id} className="p-4 flex gap-3 items-start flex-wrap" data-testid={`admin-video-row-${v.id}`}>
                <div className="flex flex-col gap-1">
                  <button className="btn btn-ghost btn-sm" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"><ArrowUp size={12} /></button>
                  <button className="btn btn-ghost btn-sm" onClick={() => move(i, 1)} disabled={i === videos.length - 1} aria-label="Move down"><ArrowDown size={12} /></button>
                </div>
                {v.thumbnail && <img src={v.thumbnail} alt="" className="rounded-md hidden sm:block" style={{ width: 112, aspectRatio: "16/9", objectFit: "cover" }} />}
                <div className="flex-1 min-w-[240px] space-y-2">
                  <input className="input" defaultValue={v.title} onBlur={(e) => e.target.value !== v.title && patch(v.id, { title: e.target.value })} data-testid={`admin-video-row-title-${v.id}`} />
                  <input className="input" defaultValue={v.vimeo_url} onBlur={(e) => e.target.value !== v.vimeo_url && patch(v.id, { vimeo_url: e.target.value })} data-testid={`admin-video-row-url-${v.id}`} />
                  <textarea className="input" rows={2} defaultValue={v.description} onBlur={(e) => e.target.value !== v.description && patch(v.id, { description: e.target.value })} />
                  <div className="flex items-center gap-3 flex-wrap text-xs" style={{ color: "var(--muted)" }}>
                    <span className={v.published ? "chip chip-success" : "chip chip-muted"} data-testid={`admin-video-state-${v.id}`}>{v.published ? "Published" : "Hidden"}</span>
                    <span>{v.duration ? formatDuration(v.duration) : v.meta_status === "ok" ? "" : "Duration unavailable — check the Vimeo privacy/domain settings"}</span>
                    <select className="input" style={{ width: "auto", padding: "0.2rem 0.5rem" }} value={v.access} onChange={(e) => patch(v.id, { access: e.target.value })} data-testid={`admin-video-row-access-${v.id}`}>
                      <option value="all">All participants</option>
                      <option value="mentorship">Mentorship only</option>
                    </select>
                    <button className="btn btn-ghost btn-sm" onClick={() => patch(v.id, { published: !v.published })} data-testid={`admin-video-publish-${v.id}`}>
                      {v.published ? <><EyeSlash size={12} /> Hide</> : <><Eye size={12} /> Publish</>}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => refresh(v.id)} title="Re-fetch duration & thumbnail from Vimeo" data-testid={`admin-video-refresh-${v.id}`}><ArrowsClockwise size={12} /> Refresh</button>
                  </div>
                </div>
                <button className="btn btn-danger btn-sm" onClick={() => remove(v)} data-testid={`admin-video-delete-${v.id}`}><Trash size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
